/**
 * Waits for a specific DOM element to appear and clicks it,
 * but only if the window width is less than 800px.
 *
 * @param {string} selector - The CSS selector of the element to wait for.
 * @param {number} maxWaitTime - Maximum wait time in milliseconds (default: 5000).
 * @param {number} checkInterval - Interval between checks in milliseconds (default: 100).
 */
function isMobileViewportForSubscription() {
	return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 749px)').matches;
}

function subscriptionDropdownOptionBaseLabel(text) {
	const t = (text || '').trim();
	const sep = ' — ';
	const idx = t.lastIndexOf(sep);
	if (idx !== -1) {
		const tail = t.slice(idx + sep.length);
		if (/\$|£|€|¥|USD|CAD|EUR|GBP/i.test(tail)) {
			return t.slice(0, idx).trim();
		}
	}
	return t;
}

/** Appends each option's data-variant-price to the visible label (prices are hidden next to radios). */
function enrichSubscriptionDropdownSelect(select) {
	if (!select || !select.matches('select.group_dropdown')) return;
	if (select.dataset.pddDropdownLabelsEnriched === 'true') return;

	select.querySelectorAll('option').forEach((opt) => {
		const price = opt.getAttribute('data-variant-price');
		if (!price) return;
		const base = subscriptionDropdownOptionBaseLabel(opt.textContent);
		opt.textContent = `${base} — ${price.trim()}`;
	});

	select.dataset.pddDropdownLabelsEnriched = 'true';
}

function autoClickWhenReady(selector, maxWaitTime = 5000, checkInterval = 100) {
	if (window.innerWidth >= 800) return;

	let waited = 0;
	const interval = setInterval(() => {
		const element = document.querySelector(selector);

		if (element) {
			element.click();
			clearInterval(interval);
		}

		waited += checkInterval;
		if (waited >= maxWaitTime) {
			clearInterval(interval);
			console.warn(`Element '${selector}' not found within ${maxWaitTime / 1000} seconds.`);
		}
	}, checkInterval);
}

/**
 * Restructures Shopify Subscriptions app block markup:
 * - Row 1: one-time + subscribe as side-by-side cards (radio on the right)
 * - Row 2: delivery frequency select full width
 */
function restructureSubscriptionPlanCard(planCard) {
	if (!planCard || planCard.dataset.pddSubscriptionRestructured === 'true') return;

	const groups = planCard.querySelectorAll(':scope > .subscription_group');
	if (groups.length < 2) return;

	const oneTimeGroup = groups[0];
	const subscribeGroup = groups[1];
	const dropdown = subscribeGroup.querySelector(':scope > select.group_dropdown');
	const titleWrapper = subscribeGroup.querySelector(':scope > .title_and_price_wrapper');

	if (!titleWrapper || !dropdown) return;

	subscribeGroup.removeChild(titleWrapper);
	subscribeGroup.removeChild(dropdown);
	if (subscribeGroup.parentNode) {
		subscribeGroup.remove();
	}

	const row = document.createElement('div');
	row.className = 'pdd-subscription-purchase-row';

	const cardOne = document.createElement('div');
	cardOne.className = 'pdd-subscription-card pdd-subscription-card--onetime';
	cardOne.appendChild(oneTimeGroup);

	const cardTwo = document.createElement('div');
	cardTwo.className = 'pdd-subscription-card pdd-subscription-card--subscribe';
	cardTwo.appendChild(titleWrapper);

	row.appendChild(cardOne);
	row.appendChild(cardTwo);

	[cardOne, cardTwo].forEach((card) => {
		card.addEventListener('click', (e) => {
			if (e.target.closest('input[type="radio"]')) return;
			if (e.target.closest('label')) return;
			const radio = card.querySelector('input[type="radio"]');
			if (radio && !radio.disabled) {
				radio.checked = true;
				radio.dispatchEvent(new Event('change', { bubbles: true }));
			}
		});
	});

	const freqRow = document.createElement('div');
	freqRow.className = 'pdd-subscription-frequency';
	freqRow.appendChild(dropdown);

	planCard.insertBefore(freqRow, planCard.firstChild);
	planCard.insertBefore(row, planCard.firstChild);

	enrichSubscriptionDropdownSelect(dropdown);

	planCard.dataset.pddSubscriptionRestructured = 'true';
}

function syncMobileSubscriptionSelectTrigger(select) {
	const shell = select.closest('.pdd-subscription-select-shell');
	if (!shell) return;
	const trigger = shell.querySelector('.pdd-subscription-select-trigger');
	const valueEl = trigger?.querySelector('.pdd-subscription-select-value');
	if (!trigger || !valueEl) return;
	const opt = select.options[select.selectedIndex];
	valueEl.textContent = opt ? opt.textContent.trim() : '';
	trigger.disabled = Boolean(select.disabled);
}

function setPddSubscriptionSelectBodyOpen(open) {
	document.documentElement.classList.toggle('pdd-subscription-select-open', open);
	document.body.classList.toggle('pdd-subscription-select-open', open);
}

/**
 * iOS Safari often dismisses the native <select> sheet after ~1–2s (viewport/layout churn).
 * On small viewports we use a button + modal list; the real select stays in the DOM and stays in sync.
 */
function attachMobileSubscriptionSelectUI(select) {
	if (!select || !isMobileViewportForSubscription()) return;

	const freq = select.closest('.pdd-subscription-frequency');
	if (!freq) return;

	if (select.dataset.pddMobileSelectUi === 'true') {
		syncMobileSubscriptionSelectTrigger(select);
		return;
	}

	if (select.parentElement?.classList.contains('pdd-subscription-select-shell')) {
		select.dataset.pddMobileSelectUi = 'true';
		syncMobileSubscriptionSelectTrigger(select);
		return;
	}

	const shell = document.createElement('div');
	shell.className = 'pdd-subscription-select-shell';

	const trigger = document.createElement('button');
	trigger.type = 'button';
	trigger.className = 'pdd-subscription-select-trigger';
	trigger.setAttribute('aria-haspopup', 'listbox');
	trigger.setAttribute('aria-expanded', 'false');
	const valueSpan = document.createElement('span');
	valueSpan.className = 'pdd-subscription-select-value';
	trigger.appendChild(valueSpan);

	const backdrop = document.createElement('div');
	backdrop.className = 'pdd-subscription-select-backdrop';
	backdrop.hidden = true;
	backdrop.setAttribute('aria-hidden', 'true');

	const panel = document.createElement('div');
	panel.className = 'pdd-subscription-select-panel';
	panel.setAttribute('role', 'listbox');
	backdrop.appendChild(panel);

	freq.insertBefore(shell, select);
	shell.appendChild(trigger);
	shell.appendChild(select);
	shell.appendChild(backdrop);

	function closePanel() {
		backdrop.hidden = true;
		backdrop.setAttribute('aria-hidden', 'true');
		trigger.setAttribute('aria-expanded', 'false');
		setPddSubscriptionSelectBodyOpen(false);
	}

	function openPanel() {
		if (select.disabled) return;

		panel.innerHTML = '';

		const header = document.createElement('div');
		header.className = 'pdd-subscription-select-panel-header';

		const closeBtn = document.createElement('button');
		closeBtn.type = 'button';
		closeBtn.className = 'pdd-subscription-select-close';
		closeBtn.textContent = 'Close';
		closeBtn.addEventListener('click', closePanel);
		header.appendChild(closeBtn);
		panel.appendChild(header);

		const list = document.createElement('div');
		list.className = 'pdd-subscription-select-options';
		panel.appendChild(list);

		Array.from(select.options).forEach((opt) => {
			if (opt.disabled) return;
			const btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'pdd-subscription-select-option';
			btn.setAttribute('role', 'option');
			btn.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
			btn.textContent = opt.textContent.trim();
			btn.addEventListener('click', () => {
				if (select.value !== opt.value) {
					select.value = opt.value;
					select.dispatchEvent(new Event('input', { bubbles: true }));
					select.dispatchEvent(new Event('change', { bubbles: true }));
				}
				syncMobileSubscriptionSelectTrigger(select);
				closePanel();
			});
			list.appendChild(btn);
		});

		backdrop.hidden = false;
		backdrop.setAttribute('aria-hidden', 'false');
		trigger.setAttribute('aria-expanded', 'true');
		setPddSubscriptionSelectBodyOpen(true);
		closeBtn.focus();
	}

	trigger.addEventListener('click', (e) => {
		e.preventDefault();
		if (!backdrop.hidden) {
			closePanel();
		} else {
			openPanel();
		}
	});

	backdrop.addEventListener('click', (e) => {
		if (e.target === backdrop) closePanel();
	});

	select.dataset.pddMobileSelectUi = 'true';
	freq.classList.add('pdd-subscription-frequency--mobile-custom');
	syncMobileSubscriptionSelectTrigger(select);
}

function attachAllMobileSubscriptionSelects() {
	document.querySelectorAll('.shopify_subscriptions_app_block select.group_dropdown').forEach((sel) => {
		attachMobileSubscriptionSelectUI(sel);
	});
}

if (typeof window !== 'undefined' && !window.__pddSubscriptionSelectEscapeBound) {
	window.__pddSubscriptionSelectEscapeBound = true;
	document.addEventListener('keydown', (e) => {
		if (e.key !== 'Escape') return;
		const backdrop = document.querySelector(
			'.shopify_subscriptions_app_block .pdd-subscription-select-backdrop:not([hidden])',
		);
		if (!backdrop) return;
		backdrop.hidden = true;
		backdrop.setAttribute('aria-hidden', 'true');
		const shell = backdrop.closest('.pdd-subscription-select-shell');
		shell?.querySelector('.pdd-subscription-select-trigger')?.setAttribute('aria-expanded', 'false');
		setPddSubscriptionSelectBodyOpen(false);
	});
}

/** Prefer one-time purchase when the Subscriptions app renders subscribe as selected. */
function ensureOneTimePurchaseDefault(block) {
	const oneTimeInput = block.querySelector('input[data-radio-type="one_time_purchase"]');
	if (!oneTimeInput || oneTimeInput.disabled || oneTimeInput.checked) return;

	oneTimeInput.checked = true;
	oneTimeInput.dispatchEvent(new Event('change', { bubbles: true }));
}

function updateSubscriptionFrequencySelectEnabled(block) {
	const select = block && block.querySelector('select.group_dropdown');
	if (!select) return;

	const oneTimeInput = block.querySelector('input[data-radio-type="one_time_purchase"]');
	const subInput = block.querySelector('input[data-radio-type="selling_plan_group"]');
	const subscriptionSelected = Boolean(subInput && subInput.checked);
	const shouldDisable = !subscriptionSelected;

	if (select.disabled !== shouldDisable) {
		select.disabled = shouldDisable;
	}

	const wrap = select.closest('.pdd-subscription-frequency');
	if (wrap) {
		wrap.classList.toggle('pdd-subscription-frequency--disabled', shouldDisable);
	}

	syncMobileSubscriptionSelectTrigger(select);
}

function updateSubscriptionCardActiveState(block) {
	const cardOne = block.querySelector('.pdd-subscription-card--onetime');
	const cardTwo = block.querySelector('.pdd-subscription-card--subscribe');
	const oneTimeInput = block.querySelector('input[data-radio-type="one_time_purchase"]');
	const subInput = block.querySelector('input[data-radio-type="selling_plan_group"]');

	if (cardOne) cardOne.classList.remove('pdd-subscription-card--active');
	if (cardTwo) cardTwo.classList.remove('pdd-subscription-card--active');

	if (oneTimeInput && oneTimeInput.checked && cardOne) {
		cardOne.classList.add('pdd-subscription-card--active');
	} else if (subInput && subInput.checked && cardTwo) {
		cardTwo.classList.add('pdd-subscription-card--active');
	}

	updateSubscriptionFrequencySelectEnabled(block);
}

function bindSubscriptionBlock(block) {
	if (!block || block.dataset.pddSubStateBound === 'true') return;
	block.dataset.pddSubStateBound = 'true';

	block.addEventListener('change', (e) => {
		if (e.target && e.target.matches && e.target.matches('input[type="radio"]')) {
			updateSubscriptionCardActiveState(block);
		}
	});

	ensureOneTimePurchaseDefault(block);
	updateSubscriptionCardActiveState(block);
}

function enrichAllSubscriptionDropdowns() {
	document.querySelectorAll('.shopify_subscriptions_app_block select.group_dropdown').forEach((sel) => {
		enrichSubscriptionDropdownSelect(sel);
	});
}

function initSubscriptionPurchaseLayout() {
	document.querySelectorAll('.shopify_subscriptions_app_block .plan_card').forEach((planCard) => {
		restructureSubscriptionPlanCard(planCard);
	});

	enrichAllSubscriptionDropdowns();

	document.querySelectorAll('.shopify_subscriptions_app_block').forEach((block) => {
		bindSubscriptionBlock(block);
		updateSubscriptionCardActiveState(block);
	});

	attachAllMobileSubscriptionSelects();
}

function initializeSubscriptionUI() {
	initSubscriptionPurchaseLayout();
}

function waitForSubscriptionElementAndInit(maxAttempts = 80, intervalMs = 250) {
	let attempts = 0;

	const tryInit = () => {
		const hasBlock = document.querySelector('.shopify_subscriptions_app_block');
		const hasPlanCard = document.querySelector('.shopify_subscriptions_app_block .plan_card');
		if (hasBlock && hasPlanCard) {
			initializeSubscriptionUI();
			return true;
		}
		return false;
	};

	if (tryInit()) return;

	const intervalId = setInterval(() => {
		if (tryInit()) {
			clearInterval(intervalId);
		} else if (++attempts >= maxAttempts) {
			clearInterval(intervalId);
		}
	}, intervalMs);
}

// Vehicle widget toggle functionality
function initializeVehicleWidgetToggle() {
	if (window.innerWidth >= 640) {
		return;
	}

	const vehicleWidgetTitles = document.querySelectorAll('.cm_vehicle-widget_title');

	if (vehicleWidgetTitles.length === 0) {
		return;
	}

	vehicleWidgetTitles.forEach((title) => {
		let parentWidget = title.closest('.cm_vehicle-widget');

		if (parentWidget) {
			const vehicleWidgetBody = parentWidget.querySelector('.cm_vehicle-widget_body');

			if (vehicleWidgetBody) {
				vehicleWidgetBody.style.display = 'none';
				vehicleWidgetBody.style.opacity = '0';
				vehicleWidgetBody.style.maxHeight = '0';
				vehicleWidgetBody.style.overflow = 'hidden';
				vehicleWidgetBody.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
			}
		}

		title.addEventListener('click', function () {
			let vehicleWidgetBody = null;
			const pw = this.closest('.cm_vehicle-widget');

			if (pw) {
				vehicleWidgetBody = pw.querySelector('.cm_vehicle-widget_body');
			} else {
				vehicleWidgetBody =
					this.parentElement.querySelector('.cm_vehicle-widget_body') ||
					(this.nextElementSibling?.classList.contains('cm_vehicle-widget_body') ? this.nextElementSibling : null);
			}

			if (vehicleWidgetBody) {
				if (vehicleWidgetBody.style.display === 'none') {
					vehicleWidgetBody.style.display = 'block';
					setTimeout(() => {
						vehicleWidgetBody.style.opacity = '1';
						vehicleWidgetBody.style.maxHeight = vehicleWidgetBody.scrollHeight + 'px';
					}, 10);
				} else {
					vehicleWidgetBody.style.opacity = '0';
					vehicleWidgetBody.style.maxHeight = '0';
					setTimeout(() => {
						vehicleWidgetBody.style.display = 'none';
					}, 300);
				}
			}
		});

		title.style.cursor = 'pointer';
		title.style.userSelect = 'none';
	});
}

function waitForVehicleWidgetAndInit(maxAttempts = 20, intervalMs = 500) {
	if (window.innerWidth >= 800) {
		return;
	}

	let attempts = 0;

	const intervalId = setInterval(() => {
		attempts++;

		const titleElements = document.querySelectorAll('.cm_vehicle-widget_title');

		if (titleElements.length > 0) {
			clearInterval(intervalId);
			new Promise((resolve) => {
				setTimeout(resolve, 1000);
			}).then(() => {
				initializeVehicleWidgetToggle();
			});
		} else if (attempts >= maxAttempts) {
			clearInterval(intervalId);
		}
	}, intervalMs);
}

document.addEventListener('DOMContentLoaded', () => {
	autoClickWhenReady('.cm_vehicle-widget_header');

	waitForSubscriptionElementAndInit();

	document.addEventListener('shopify:section:load', () => {
		document.querySelectorAll('.shopify_subscriptions_app_block select.group_dropdown').forEach((sel) => {
			delete sel.dataset.pddDropdownLabelsEnriched;
			delete sel.dataset.pddMobileSelectUi;
		});
		document.querySelectorAll('.shopify_subscriptions_app_block .plan_card').forEach((pc) => {
			delete pc.dataset.pddSubscriptionRestructured;
		});
		document.querySelectorAll('.shopify_subscriptions_app_block').forEach((b) => {
			delete b.dataset.pddSubStateBound;
		});
		initSubscriptionPurchaseLayout();
	});

	waitForVehicleWidgetAndInit();
});
